// src/NeighborhoodMap.js
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { db, storage } from './firebase.js';
import { collection, getDocs, setDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Import Leaflet marker images as modules (fixes 'require is not defined' in browser)
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Custom house icon setup
import houseIconPng from './assets/house-icon.png';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const houseIcon = new L.Icon({
  iconUrl: houseIconPng,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
  shadowUrl: markerShadow,
  shadowSize: [41, 41],
});

// Define lots with exact coordinates provided by user (removed boundaries since lines are disabled)
const lots = [
  {
    lot: 'Lot 1',
    address: '111 Franklin Branch Road, Topton, NC',
    owners: 'Ana Jones, Richard Clay',
    contact: '(561) 718-1212, (386) 546-5979, Flrealtorana@gmail.com, richclay510@gmail.com',
    position: [35.251132, -83.696922],
  },
  {
    lot: 'Lot 2',
    address: '191 Franklin Branch Road, Topton, NC',
    owners: 'Jack Cartright, Patty Cartright',
    contact: '(770) 480-0123, jackc7161@att.net',
    position: [35.250254, -83.697007],
  },
  {
    lot: 'Lot 3',
    address: '380 Franklin Branch Road, Topton, NC',
    owners: 'Phillip Cloninger, Debra Cloninger',
    contact: '(704) 957-4344, mnpineville@comporium.net',
    position: [35.250469, -83.695126],
  },
  {
    lot: 'Lot 4',
    address: '425 Franklin Branch Road, Topton, NC',
    owners: 'Diane Whinham',
    contact: '(248) 495-4746, casabellallc@yahoo.com',
    position: [35.249441, -83.695453],
  },
  {
    lot: 'Lot 5',
    address: '36 Mountain Chase, Topton, NC',
    owners: 'David Sutton, Sheri Sutton',
    contact: '(727) 243-1579, (727) 641-3068, david.sutton5@outlook.com, sheribos@yahoo.com',
    position: [35.249882, -83.694804],
  },
  {
    lot: 'Lot 6',
    address: '210 Franklin Branch Road, Topton, NC',
    owners: 'George Trosset, Nihla Trosset',
    contact: '(321) 223-6400, (321) 223-4298, georgetrosset@me.com, nihlatrosset@me.com',
    position: [35.249087, -83.696435],
  },
  {
    lot: 'Lot 7',
    address: '61 Mountain Chase, Topton, NC',
    owners: 'Mike Chafin, Nancy Chafin',
    contact: '(706) 244-1144, (706) 894-6749, mikeechafin@gmail.com, nancyschafin@gmail.com',
    position: [35.249327, -83.694592],
  },
  {
    lot: 'Lot 8',
    address: '593 Franklin Branch Road, Topton, NC',
    owners: 'Mike Chafin, Nancy Chafin',
    contact: '(706) 244-1144, (706) 894-6749, mikeechafin@gmail.com, nancyschafin@gmail.com',
    position: [35.248242, -83.695590],
  },
];

function PopupContent({ lot, lotId, imageUrl, isLoading, reloadKey, isAdmin, handleImageUpload, user }) {
  const map = useMap();
  let src = imageUrl;
  if (src) {
    const timestamp = new Date().getTime();
    src += (src.includes('?') ? '&' : '?') + 'ts=' + timestamp;
  }

  return (
    <>
      <strong>{lot.lot}</strong><br />
      Address: {lot.address}<br />
      {imageUrl && <img key={reloadKey} src={src} alt={lot.lot} style={{ width: '100%', marginTop: '10px' }} onError={(e) => console.error('Image load error for ' + lotId + ': URL = ' + e.target.src)} />}
      {user && (
        <>
          Owners: {lot.owners}<br />
          Contact: {lot.contact}<br />
          {isLoading && <p>Uploading image...</p>}
          {isAdmin && (
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(lotId, e.target.files[0])}
              style={{ marginTop: '10px' }}
            />
          )}
        </>
      )}
      <button onClick={() => map.closePopup()} style={{ marginTop: '10px' }}>Close</button>
    </>
  );
}

// New component to handle map resizing on mount
function MapResizer() {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 100);
  }, [map]);
  return null;
}

function NeighborhoodMap({ isMobile, user, isAdmin }) {
  const center = [35.250, -83.696];
  const zoom = 16;
  const [lotImages, setLotImages] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [forceReload, setForceReload] = useState({});

  useEffect(() => {
    const fetchLotImages = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'lotImages'));
        const images = {};
        querySnapshot.forEach((doc) => {
          images[doc.id] = doc.data().imageUrl;
        });
        setLotImages(images);
      } catch (error) {
        console.error('Error fetching lot images:', error);
      }
    };
    fetchLotImages();
  }, []);

  const refetchImages = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'lotImages'));
      const images = {};
      querySnapshot.forEach((doc) => {
        images[doc.id] = doc.data().imageUrl;
      });
      setLotImages(images);
    } catch (error) {
      console.error('Error refetching lot images:', error);
    }
  };

  const handleImageUpload = async (lotId, file) => {
    if (!file || !isAdmin) return;
    setLoadingImages((prev) => ({ ...prev, [lotId]: true }));
    try {
      const ext = file.name.split('.').pop(); // Get file extension
      const storageRef = ref(storage, `lotImages/${lotId}.${ext}`);
      await uploadBytes(storageRef, file, { contentType: file.type }); // Set contentType
      const imageUrl = await getDownloadURL(storageRef);
      await setDoc(doc(db, 'lotImages', lotId), { imageUrl });
      await refetchImages();
      setForceReload((prev) => ({ ...prev, [lotId]: !prev[lotId] }));
      console.log(`Image uploaded for ${lotId}`);
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setLoadingImages((prev) => ({ ...prev, [lotId]: false }));
    }
  };

  return (
    <div className={`map-container ${isMobile ? 'map-container-mobile' : ''}`}>
      <MapContainer center={center} zoom={zoom} style={{ height: '100%', width: '100%' }}>
        <MapResizer /> {/* Added to fix potential sizing issue on initial render */}
        <LayersControl position="topright">
          <LayersControl.BaseLayer name="Street Map">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Topographic Map">
            <TileLayer
              url="https://a.tile.opentopomap.org/{z}/{x}/{y}.png"
              attribution='© <a href="https://opentopomap.org/about">OpenTopoMap</a> (CC-BY-SA)'
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer checked name="Satellite View">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='© Esri & contributors'
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {lots.map((lot, index) => {
          const lotId = lot.lot.replace(' ', '');
          const imageUrl = lotImages[lotId];
          const isLoading = loadingImages[lotId];
          const reloadKey = forceReload[lotId];

          return (
            <Marker
              key={index}
              icon={houseIcon}
              position={lot.position}
              title={lot.address}
            >
              <Popup autoClose={false} closeOnClick={false}>
                <PopupContent
                  lot={lot}
                  lotId={lotId}
                  imageUrl={imageUrl}
                  isLoading={isLoading}
                  reloadKey={reloadKey}
                  isAdmin={isAdmin}
                  handleImageUpload={handleImageUpload}
                  user={user}
                />
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}

export default NeighborhoodMap;